import { DfiReportResponse, TqEnAnnualReportResponse, DfiAnnualReport } from 'types';

import { formatKpiData } from './formatKpiData';

import {
  useTqEnAgreedExtensionStatsLabels,
  useTqEnAppealConsultationStatsLabels,
  useTqEnTimeframeExceededStatsLabels,
  useTqEnTimelinessResponseStatsLabels,
} from '../../../TqEnAnnualReport/hooks';
import { useThemedValueSwitch } from '@tq/ui-components/dist/hooks';
import { TFunction } from 'i18next';
import { tqEnTitles } from './tqTitles';

export const useTQFormattedDfiReport = (
  reportResponse: DfiReportResponse | TqEnAnnualReportResponse,
  t: TFunction
): {
  tqFormattedReport: DfiAnnualReport | undefined;
  tqEnMetrics: Record<string, unknown> | undefined;
  tqTitles: Record<string, string> | undefined;
} => {
  const tqEnAgreedExtensionStatsLabels = useTqEnAgreedExtensionStatsLabels();

  const tqEnAppealConsultationStatsLabels = useTqEnAppealConsultationStatsLabels();

  const tqEnTimeframeExceededStatsLabels = useTqEnTimeframeExceededStatsLabels();

  const tqEnTimelinessResponseStatsLabels = useTqEnTimelinessResponseStatsLabels();

  const { isTQ } = useThemedValueSwitch();

  let formattedReport = undefined;
  let tqEnMetrics = undefined;

  if (isTQ && reportResponse && 'tqEnTimelinessResponseStats' in reportResponse) {
    formattedReport = {
      ...reportResponse,
      statutoryTimelinessResponseStats: formatKpiData(
        reportResponse.tqEnTimelinessResponseStats,
        tqEnTimelinessResponseStatsLabels
      ),
      lateResponseStatItems: reportResponse.lateResponseStatItems,
      statutoryTimeframeExceededStats: formatKpiData(
        reportResponse.tqEnTimeframeExceededStats,
        tqEnTimeframeExceededStatsLabels
      ),
      agreedExtensionStats: formatKpiData(
        reportResponse.tqEnAgreedExtensionStats,
        tqEnAgreedExtensionStatsLabels
      ),
      outstandingResponseStats: formatKpiData(
        {
          totalConsultationsWithOutstandingResponses:
            reportResponse.tqEnTimelinessResponseStats
              .totalConsultationsWithOutstandingResponses,
        },
        tqEnTimelinessResponseStatsLabels
      ),
      totalEiaConsultationsReceived: formatKpiData(
        {
          totalEiaConsultationsReceived:
            reportResponse.tqEnAppealConsultationStats.totalAppealConsultationsReceived,
        },
        tqEnAppealConsultationStatsLabels
      ),
      averageDaysTakenToRespondToEiaConsultations: formatKpiData(
        {
          averageDaysTakenToRespondToEiaConsultations:
            reportResponse.tqEnAppealConsultationStats
              .averageDaysTakenToRespondToAppealConsultations,
        },
        tqEnAppealConsultationStatsLabels
      ),
      nonStatutoryTimelinessResponseStats: null,
    };

    tqEnMetrics = {
      timelinessResponseStats: formattedReport.statutoryTimelinessResponseStats,
      lateResponseStatItems: formattedReport.lateResponseStatItems,
      timeframeExceededStats: formattedReport.statutoryTimeframeExceededStats,
      agreedExtensionStats: formattedReport.agreedExtensionStats,
      outstandingResponseStats: formattedReport.outstandingResponseStats,
      totalAppealConsultationsReceived: formattedReport.totalEiaConsultationsReceived,
      averageDaysTakenToRespondToAppealConsultations:
        formattedReport.averageDaysTakenToRespondToEiaConsultations,
    };
  }

  return {
    tqFormattedReport: formattedReport,
    tqEnMetrics: tqEnMetrics,
    tqTitles: isTQ ? tqEnTitles(t) : undefined,
  };
};
